import { useGetList, useTranslate } from "ra-core";
import { Plus } from "lucide-react";
import { Link, matchPath, useLocation } from "react-router";
import { Button } from "@/components/ui/button";

import useAppBarHeight from "../misc/useAppBarHeight";
import type { Company, SponsoringKind } from "../types";
import { SponsoringCreate } from "./SponsoringCreate";

/**
 * Empty state of an overview: invite to add the first sponsoring, or to add
 * a company first when there is none yet.
 */
export const SponsoringEmpty = ({
  basePath = "/sponsorings",
  kind = "sponsoring",
  createLabel = "resources.sponsorings.action.create",
  defaultValues,
}: {
  basePath?: string;
  kind?: SponsoringKind;
  createLabel?: string;
  defaultValues?: Record<string, unknown>;
}) => {
  const translate = useTranslate();
  const location = useLocation();
  const matchCreate = matchPath(`${basePath}/create`, location.pathname);
  const appbarHeight = useAppBarHeight();

  const { data: companies, isPending } = useGetList<Company>("companies", {
    pagination: { page: 1, perPage: 1 },
  });

  if (isPending) return null;
  const hasCompany = !!companies?.length;

  return (
    <div
      className="flex flex-col justify-center items-center gap-8"
      style={{ height: `calc(100dvh - ${appbarHeight}px)` }}
    >
      <img
        src="./img/empty.svg"
        alt=""
        className="max-h-48"
        aria-hidden="true"
      />
      <div className="flex flex-col items-center gap-2 text-center">
        <h3 className="text-lg font-bold">
          {translate("resources.sponsorings.empty.title")}
        </h3>
        <p className="text-sm text-muted-foreground">
          {translate(
            hasCompany
              ? "resources.sponsorings.empty.description"
              : "resources.sponsorings.empty.need_company",
          )}
        </p>
      </div>
      {hasCompany ? (
        <>
          <Button asChild variant="outline">
            <Link to={`${basePath}/create`}>
              <Plus />
              {translate(createLabel)}
            </Link>
          </Button>
          <SponsoringCreate
            open={!!matchCreate}
            basePath={basePath}
            defaultValues={{ kind, ...defaultValues }}
          />
        </>
      ) : (
        <Button asChild>
          <Link to="/companies/create">
            {translate("resources.sponsorings.empty.create_company")}
          </Link>
        </Button>
      )}
    </div>
  );
};
